import React from "react";
import { Copy, Check, Download, Link2, FileSignature, QrCode } from "lucide-react";
import QRCode from "qrcode";
import api from "../../lib/api";

const CATEGORIES = {
  portrait: { label: "Portrait & Branding", color: "#c8821f" },
  maternite: { label: "Maternité & Bébé", color: "#e8488a" },
  celebration: { label: "Célébration", color: "#8a2a2a" },
  location: { label: "Location studio", color: "#2a4e9b" },
  // legacy fallbacks
  seance: { label: "Séance photo", color: "#c8821f" },
  evenement: { label: "Évènement", color: "#f472b6" },
};

export default function AdminLiensPartage() {
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [copiedSlug, setCopiedSlug] = React.useState("");
  const [qrDataUrls, setQrDataUrls] = React.useState({}); // {slug: dataUrl}

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  React.useEffect(() => {
    api.get("/services")
      .then(r => setServices(r.data))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  const buildUrl = React.useCallback((svc) => {
    if (svc.requires_quote) return `${origin}/reservation/devis/${svc.slug}`;
    return `${origin}/r/${svc.slug}`;
  }, [origin]);

  const buildGiftUrl = React.useCallback((svc) => {
    if (svc.requires_quote) return null;
    return `${origin}/offrir/${svc.slug}`;
  }, [origin]);

  // Generate QR codes once services are loaded
  React.useEffect(() => {
    if (!services.length) return;
    let cancelled = false;
    (async () => {
      const map = {};
      for (const svc of services) {
        try {
          const url = buildUrl(svc);
          map[svc.slug] = await QRCode.toDataURL(url, {
            width: 512, margin: 2,
            color: { dark: "#1a1a1a", light: "#ffffff" },
            errorCorrectionLevel: "H",
          });
          const gurl = buildGiftUrl(svc);
          if (gurl) {
            map[`gift-${svc.slug}`] = await QRCode.toDataURL(gurl, {
              width: 512, margin: 2,
              color: { dark: "#c8821f", light: "#ffffff" },
              errorCorrectionLevel: "H",
            });
          }
        } catch {}
      }
      if (!cancelled) setQrDataUrls(map);
    })();
    return () => { cancelled = true; };
  }, [services, buildUrl, buildGiftUrl]);

  const copyLink = async (svc) => {
    const url = buildUrl(svc);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(svc.slug);
      setTimeout(() => setCopiedSlug(""), 2000);
    } catch {
      // fallback: temporary input
      const el = document.createElement("input");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopiedSlug(svc.slug);
      setTimeout(() => setCopiedSlug(""), 2000);
    }
  };

  const downloadQR = (svc) => {
    const dataUrl = qrDataUrls[svc.slug];
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `QR_${svc.slug}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Group services by category (ordered)
  const grouped = React.useMemo(() => {
    const order = ["portrait", "maternite", "celebration", "location"];
    const out = {};
    for (const s of services) {
      const key = s.category || "portrait";
      if (!out[key]) out[key] = [];
      out[key].push(s);
    }
    // Sort each group by `ordre`
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
    }
    // Return ordered entries
    const ordered = {};
    for (const k of order) if (out[k]) ordered[k] = out[k];
    for (const k of Object.keys(out)) if (!(k in ordered)) ordered[k] = out[k];
    return ordered;
  }, [services]);

  return (
    <div className="text-text-cream" data-testid="admin-liens-partage">
      <div className="mb-10">
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif text-4xl">Liens de partage</h1>
        <p className="text-text-cream/50 mt-2 text-sm font-light max-w-2xl">
          Un lien direct par service pour tes posts Instagram, e-mails, flyers. Le client arrive directement
          sur le calendrier du bon service — sans friction. QR codes téléchargeables pour impression.
        </p>
      </div>

      {loading && (
        <div className="text-text-cream/60 font-light text-sm" data-testid="loading-services">
          Chargement des services…
        </div>
      )}

      {!loading && Object.keys(grouped).length === 0 && (
        <div className="text-text-cream/60 font-light text-sm">Aucun service trouvé.</div>
      )}

      {Object.entries(grouped).map(([cat, items]) => {
        const meta = CATEGORIES[cat] || CATEGORIES.seance;
        return (
          <div key={cat} className="mb-12">
            <div className="flex items-center gap-3 mb-5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: meta.color }}
              />
              <h2 className="font-serif text-xl">{meta.label}</h2>
              <span className="text-text-cream/40 text-xs font-light">({items.length})</span>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map((svc) => {
                const url = buildUrl(svc);
                const copied = copiedSlug === svc.slug;
                return (
                  <div
                    key={svc.slug}
                    className="bg-white/[0.03] border border-white/10 rounded p-5 flex flex-col"
                    data-testid={`service-link-card-${svc.slug}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="font-serif text-lg leading-tight">{svc.name}</div>
                        <div className="text-text-cream/40 text-xs mt-1 font-light">
                          {svc.duration_min} min · {svc.price_chf} CHF
                        </div>
                      </div>
                      {svc.requires_quote && (
                        <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-[0.18em] text-rose-300/80 border border-rose-400/30 bg-rose-500/10 px-2 py-1 rounded">
                          <FileSignature size={10} /> Devis
                        </span>
                      )}
                    </div>

                    <div className="flex items-start gap-3 mb-4">
                      <div className="shrink-0 bg-white rounded p-2" style={{ width: 96, height: 96 }}>
                        {qrDataUrls[svc.slug] ? (
                          <img
                            src={qrDataUrls[svc.slug]}
                            alt={`QR ${svc.name}`}
                            className="w-full h-full"
                            data-testid={`qr-img-${svc.slug}`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-neutral-400">
                            <QrCode size={28} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="eyebrow text-text-cream/50 text-[0.6rem] mb-1 flex items-center gap-1">
                          <Link2 size={10} /> Lien direct
                        </div>
                        <div
                          className="text-xs font-mono bg-black/30 border border-white/10 rounded px-2 py-2 break-all text-text-cream/80"
                          data-testid={`link-url-${svc.slug}`}
                        >
                          {url}
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto flex gap-2">
                      <button
                        onClick={() => copyLink(svc)}
                        className={`flex-1 inline-flex items-center justify-center gap-2 text-xs tracking-wide py-2.5 rounded border transition ${
                          copied
                            ? "bg-accent-light/20 border-accent-light/40 text-accent-light"
                            : "bg-white/[0.04] border-white/15 text-text-cream hover:bg-white/10"
                        }`}
                        data-testid={`copy-link-${svc.slug}`}
                      >
                        {copied ? (
                          <>
                            <Check size={14} /> Copié !
                          </>
                        ) : (
                          <>
                            <Copy size={14} /> Copier le lien
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => downloadQR(svc)}
                        disabled={!qrDataUrls[svc.slug]}
                        className="inline-flex items-center gap-2 text-xs tracking-wide py-2.5 px-3 rounded border border-accent-deep/40 bg-accent-deep/15 text-accent-light hover:bg-accent-deep/25 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        data-testid={`download-qr-${svc.slug}`}
                        title="Télécharger le QR code en PNG"
                      >
                        <Download size={14} /> QR
                      </button>
                    </div>

                    {!svc.requires_quote && (
                      <div className="mt-3 pt-3 border-t border-accent-deep/20">
                        <div className="eyebrow text-accent-deep text-[0.6rem] mb-2 inline-flex items-center gap-1">
                          🎁 Lien cadeau
                        </div>
                        <div
                          className="text-[0.7rem] font-mono bg-black/30 border border-accent-deep/20 rounded px-2 py-1.5 break-all text-text-cream/80 mb-2"
                          data-testid={`gift-link-${svc.slug}`}
                        >
                          {buildGiftUrl(svc)}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              const url = buildGiftUrl(svc);
                              try {
                                await navigator.clipboard.writeText(url);
                                setCopiedSlug(`gift-${svc.slug}`);
                                setTimeout(() => setCopiedSlug(""), 2000);
                              } catch {}
                            }}
                            className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[0.7rem] py-1.5 rounded border transition ${
                              copiedSlug === `gift-${svc.slug}`
                                ? "bg-accent-light/20 border-accent-light/40 text-accent-light"
                                : "bg-white/[0.03] border-accent-deep/30 text-text-cream/80 hover:bg-accent-deep/15"
                            }`}
                            data-testid={`copy-gift-link-${svc.slug}`}
                          >
                            {copiedSlug === `gift-${svc.slug}` ? <><Check size={10} /> Copié</> : <><Copy size={10} /> Copier</>}
                          </button>
                          <button
                            onClick={() => {
                              const dataUrl = qrDataUrls[`gift-${svc.slug}`];
                              if (!dataUrl) return;
                              const a = document.createElement("a");
                              a.href = dataUrl;
                              a.download = `QR_cadeau_${svc.slug}.png`;
                              a.click();
                            }}
                            disabled={!qrDataUrls[`gift-${svc.slug}`]}
                            className="inline-flex items-center gap-1 text-[0.7rem] py-1.5 px-2.5 rounded border border-accent-deep/40 bg-accent-deep/10 text-accent-light hover:bg-accent-deep/20 disabled:opacity-40 transition"
                            data-testid={`download-gift-qr-${svc.slug}`}
                          >
                            <Download size={10} /> QR
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
